import { Component, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ConfigurationChangeHistory, ConfigurationWaterfall, ConfigurationWaterfallService } from '../configuration-waterfall.service';
import { DiffView } from '../diff-view/diff-view';
import {
  DiffLine, Environment, PayloadKind, RenderMode, TextFn,
  authorColors, environmentLabel, environments, initials, relativeTime, sideLines,
} from '../configuration-view';

export interface HistoryCheckout {
  entry: ConfigurationChangeHistory;
  environment: Environment;
  version: number;
}

@Component({
  selector: 'app-history-drawer',
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, TranslocoPipe, DiffView],
  templateUrl: './history-drawer.html',
  styleUrl: './history-drawer.scss',
})
export class HistoryDrawer {
  private readonly api = inject(ConfigurationWaterfallService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  readonly row = input.required<ConfigurationWaterfall>();
  readonly environment = input.required<Environment>();
  readonly renderMode = model<RenderMode>('rendered');
  readonly closed = output<void>();
  readonly checkout = output<HistoryCheckout>();

  readonly environments = environments;
  readonly historyEnvironment = signal<Environment>('stable');
  readonly historyByEnvironment = signal<Record<Environment, ConfigurationChangeHistory[]>>({
    stable: [], alfa: [], beta: [], prod: [],
  });
  readonly historyLoading = signal(false);
  readonly historyError = signal<string | null>(null);
  readonly historyExpanded = signal<string[]>([]);
  private historyRequest = 0;
  private readonly text: TextFn = key => this.transloco.translate(key);

  constructor() {
    effect(() => {
      const row = this.row();
      const environment = this.environment();
      this.historyEnvironment.set(environment);
      this.load(row);
    });
  }

  label(environment: Environment): string {
    return environmentLabel(environment);
  }

  selectHistoryEnvironment(environment: Environment): void {
    this.historyEnvironment.set(environment);
    this.historyExpanded.set([]);
  }

  checkoutEntry(entry: ConfigurationChangeHistory, event: Event): void {
    event.stopPropagation();
    this.checkout.emit({ entry, environment: this.historyEnvironment(), version: this.historyVersion(entry) });
  }

  toggleHistory(id: string): void {
    const current = this.historyExpanded();
    this.historyExpanded.set(current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  }

  isHistoryExpanded(id: string): boolean {
    return this.historyExpanded().includes(id);
  }

  historyEntries(): ConfigurationChangeHistory[] {
    return this.historyByEnvironment()[this.historyEnvironment()] ?? [];
  }

  historyCount(environment: Environment): number {
    return this.historyByEnvironment()[environment]?.length ?? 0;
  }

  historyLatest(environment: Environment): ConfigurationChangeHistory | null {
    return this.historyByEnvironment()[environment]?.[0] ?? null;
  }

  historyVersion(entry: ConfigurationChangeHistory): number {
    const ordered = [...this.historyEntries()].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
    return ordered.findIndex(item => item.id === entry.id) + 1;
  }

  historyShortId(id: string): string {
    return id.replaceAll('-', '').slice(0, 7);
  }

  historyChangeLabel(entry: ConfigurationChangeHistory): string {
    const parts: string[] = [];
    if (entry.applicationData !== entry.previousApplicationData) parts.push(this.text('fields.applicationData'));
    if (entry.apiCoreData !== entry.previousApiCoreData) parts.push(this.text('fields.apiCore'));
    return parts.join(' · ') || this.text('fields.noContentChange');
  }

  historyAuthor(entry: ConfigurationChangeHistory): { name: string; initials: string | null; ago: string | null; when: string; color: string } {
    const raw = entry.changedBy?.trim() ?? '';
    const name = raw || this.text('history.unknownAuthor');
    const date = new Date(entry.createdAt);
    const valid = !Number.isNaN(date.getTime());
    const locale = this.transloco.getActiveLang();
    return {
      name,
      initials: entry.changedBy ? initials(entry.changedBy) : null,
      ago: valid ? relativeTime(date, locale) : null,
      when: valid ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date) : entry.createdAt,
      color: authorColors[[...(raw || 'unknown')].reduce((sum, char) => sum + char.charCodeAt(0), 0) % authorColors.length],
    };
  }

  historyKinds(entry: ConfigurationChangeHistory): PayloadKind[] {
    const kinds: PayloadKind[] = [];
    if (entry.applicationData !== entry.previousApplicationData) kinds.push('Application');
    if (entry.apiCoreData !== entry.previousApiCoreData) kinds.push('ApiCore');
    if (kinds.length === 0) kinds.push(this.row().type === 'api-core-configuration' ? 'ApiCore' : 'Application');
    return kinds;
  }

  historyKindLabel(kind: PayloadKind): string {
    return kind === 'ApiCore' ? this.text('fields.apiCoreData') : this.text('fields.applicationData');
  }

  historyLines(entry: ConfigurationChangeHistory, kind: PayloadKind, side: 'left' | 'right'): DiffLine[] {
    const before = kind === 'ApiCore' ? entry.previousApiCoreData : entry.previousApplicationData;
    const after = kind === 'ApiCore' ? entry.apiCoreData : entry.applicationData;
    return sideLines(before, after, kind === 'ApiCore', side, this.renderMode(), this.text);
  }

  private load(row: ConfigurationWaterfall): void {
    const requestId = ++this.historyRequest;
    this.historyByEnvironment.set({ stable: [], alfa: [], beta: [], prod: [] });
    this.historyExpanded.set([]);
    this.historyError.set(null);
    this.historyLoading.set(true);
    let failed = false;
    const load = (source: Environment) => this.api.history(row, source).pipe(catchError((error: HttpErrorResponse) => {
      if (error.status !== 404) failed = true;
      return of([] as ConfigurationChangeHistory[]);
    }));
    forkJoin({
      stable: load('stable'),
      alfa: load('alfa'),
      beta: load('beta'),
      prod: load('prod'),
    }).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
      if (requestId === this.historyRequest) this.historyLoading.set(false);
    })).subscribe(byEnvironment => {
      if (requestId !== this.historyRequest) return;
      this.historyByEnvironment.set(byEnvironment);
      if (failed) this.historyError.set('errors.historyPartial');
    });
  }
}
